/**
 * Registering the service worker, and the one rule that governs updates.
 *
 * > **An update must never destroy unsaved work.** (09-frontend.md)
 *
 * A waiting worker is therefore never activated automatically. Activating one reloads every open
 * tab, and a reload in the middle of planning a funeral would discard whatever the person had typed
 * and not yet saved. The page is told an update is ready and the choice is theirs; if they never
 * choose, it applies at the next natural reload, which costs nothing.
 *
 * Registration is skipped in development. A stale worker serving a cached shell over Vite's HMR is
 * the kind of bug that wastes an afternoon and teaches nothing.
 */

/** Fired when a new worker is installed and waiting. The shell listens and offers to apply it. */
export const UPDATE_READY_EVENT = 'pius:update-ready';

export function registerServiceWorker(): void {
  if (import.meta.env.DEV) return;
  if (!('serviceWorker' in navigator)) return;

  window.addEventListener('load', () => {
    void navigator.serviceWorker
      .register('/sw.js', { scope: '/' })
      .then((registration) => {
        // Already waiting from a previous visit.
        if (registration.waiting) announce();

        registration.addEventListener('updatefound', () => {
          const installing = registration.installing;
          if (!installing) return;

          installing.addEventListener('statechange', () => {
            // `controller` present means this is an update rather than a first install — on a first
            // install there is nothing to interrupt and nothing to announce.
            if (installing.state === 'installed' && navigator.serviceWorker.controller) {
              announce();
            }
          });
        });
      })
      .catch(() => {
        // A failed registration costs offline shell caching and nothing else. The application works
        // exactly as it does for a browser with no service worker support at all.
      });
  });

  // The reload happens here, once — after the new worker takes control, and only because the page
  // asked it to via `applyUpdate`.
  let reloading = false;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (reloading) return;
    reloading = true;
    window.location.reload();
  });
}

function announce(): void {
  window.dispatchEvent(new CustomEvent(UPDATE_READY_EVENT));
}

/** Called only from a control the person pressed. Never on a timer, never on navigation. */
export async function applyUpdate(): Promise<void> {
  if (!('serviceWorker' in navigator)) return;

  const registration = await navigator.serviceWorker.getRegistration();
  registration?.waiting?.postMessage('skip-waiting');
}
